import React from 'react';

interface Release {
  id: string | number;
  date: string;
  title: string;
  hasLocalChanges: boolean;
  footprint?: {
    type: string;
    coordinates: number[][][] | number[][];
  } | null;
  tileTemplate?: string;
  thumbUrl?: string;
  source?: 'esri' | 'eosda'; // Internal tracking only
  view_id?: string;
  cloudCoverage?: number;
  dataCoveragePercentage?: number;
}

interface WaybackTimelineProps {
  releases: Release[];
  activeId: string | number | null;
  onSelect: (id: string | number) => void;
  onlyLocal: boolean;
  setOnlyLocal: (value: boolean) => void;
  loading: boolean;
  beforeReleaseId?: string | number | null;
  afterReleaseId?: string | number | null;
  onBeforeSelect?: (id: string | number | null) => void;
  onAfterSelect?: (id: string | number | null) => void;
}

const WaybackTimeline: React.FC<WaybackTimelineProps> = ({
  releases,
  activeId,
  onSelect,
  onlyLocal,
  setOnlyLocal,
  beforeReleaseId,
  afterReleaseId,
  onBeforeSelect,
  onAfterSelect
}) => {
  // Removed unused state - using parent state directly via props
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const getYear = (dateString: string) => {
    try {
      return new Date(dateString).getFullYear();
    } catch {
      return 'Unknown';
    }
  };

  // Group releases by year
  const releasesByYear = releases.reduce((acc, release) => {
    const year = getYear(release.date);
    if (!acc[year]) {
      acc[year] = [];
    }
    acc[year].push(release);
    return acc;
  }, {} as Record<string, Release[]>);

  const sortedYears = Object.keys(releasesByYear).sort((a, b) => parseInt(b) - parseInt(a));

  const isSentinel = (r: Release) =>
    String(r.id).startsWith('eosda_') || String(r.id).startsWith('element84_') ||
    (r.source as string) === 'eosda' || (r.source as string) === 'copernicus';

  return (
    <div className="space-y-3">
      {/* Only-local filter */}
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="onlyLocalTimeline"
          checked={onlyLocal}
          onChange={(e) => setOnlyLocal(e.target.checked)}
          className="rounded border-border w-3.5 h-3.5"
        />
        <label htmlFor="onlyLocalTimeline" className="text-xs text-muted-foreground">
          Only versions with local changes
        </label>
      </div>

      {/* Before / After selection summary */}
      {(beforeReleaseId || afterReleaseId) && (
        <div className="flex flex-wrap gap-1.5 p-2 bg-primary/5 border border-primary/20 rounded">
          {beforeReleaseId && (() => {
            const rel = releases.find(r => r.id === beforeReleaseId);
            return rel ? (
              <span className="bg-blue-700 text-white text-[10px] px-2 py-0.5 rounded">
                B: {formatDate(rel.date)}
              </span>
            ) : null;
          })()}
          {afterReleaseId && (() => {
            const rel = releases.find(r => r.id === afterReleaseId);
            return rel ? (
              <span className="bg-green-700 text-white text-[10px] px-2 py-0.5 rounded">
                A: {formatDate(rel.date)}
              </span>
            ) : null;
          })()}
        </div>
      )}

      {/* Release list */}
      {releases.length === 0 ? null : (
        <div className="space-y-3">
          {sortedYears.map((year) => (
            <div key={year}>
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider border-b border-border pb-0.5 mb-1">
                {year}
              </p>
              <div className="space-y-1">
                {releasesByYear[year].map((release) => {
                  const isActive = activeId === release.id;
                  const isBefore = beforeReleaseId === release.id;
                  const isAfter = afterReleaseId === release.id;
                  const sentinel = isSentinel(release);
                  return (
                    <div
                      key={release.id}
                      className={`rounded border text-xs transition-colors ${
                        isActive
                          ? 'bg-primary/10 border-primary/30'
                          : isBefore
                          ? 'bg-blue-950/40 border-blue-700/40'
                          : isAfter
                          ? 'bg-green-950/40 border-green-700/40'
                          : 'bg-card border-border hover:bg-accent/40'
                      }`}
                    >
                      {/* Main row — clickable to make active */}
                      <div
                        className="flex items-center gap-2 px-2.5 py-2 cursor-pointer"
                        onClick={() => onSelect(release.id)}
                      >
                        {/* Source badge */}
                        <span className={`shrink-0 text-[9px] px-1 py-0.5 rounded font-medium ${
                          sentinel ? 'bg-cyan-900/60 text-cyan-300' : 'bg-slate-700/60 text-slate-400'
                        }`}>
                          {sentinel ? 'S2' : 'ESRI'}
                        </span>
                        {/* Date */}
                        <span className={`font-medium flex-1 truncate ${isActive ? 'text-primary' : 'text-foreground'}`}>
                          {formatDate(release.date)}
                        </span>
                        {/* Indicators */}
                        <div className="flex items-center gap-1 shrink-0">
                          {release.hasLocalChanges && (
                            <div className="w-1.5 h-1.5 bg-green-500 rounded-full" title="Local changes" />
                          )}
                          {release.cloudCoverage !== undefined && (
                            <span className={`text-[9px] ${release.cloudCoverage > 20 ? 'text-amber-400' : 'text-slate-500'}`}>
                              {release.cloudCoverage.toFixed(0)}%☁
                            </span>
                          )}
                          {isActive && <span className="text-[9px] text-primary font-semibold">●</span>}
                        </div>
                      </div>
                      {/* Before / After buttons */}
                      <div className="flex gap-1 px-2.5 pb-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (onBeforeSelect) onBeforeSelect(isBefore ? null : release.id);
                          }}
                          className={`flex-1 text-[10px] py-0.5 rounded font-medium transition-colors ${
                            isBefore
                              ? 'bg-blue-600 text-white'
                              : 'bg-muted text-muted-foreground hover:bg-muted/80'
                          }`}
                        >
                          {isBefore ? 'Before ✓' : 'Before'}
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (onAfterSelect) onAfterSelect(isAfter ? null : release.id);
                          }}
                          className={`flex-1 text-[10px] py-0.5 rounded font-medium transition-colors ${
                            isAfter
                              ? 'bg-green-600 text-white'
                              : 'bg-muted text-muted-foreground hover:bg-muted/80'
                          }`}
                        >
                          {isAfter ? 'After ✓' : 'After'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default WaybackTimeline;
